import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRefFunction } from '../../utils/hooks';
import { appActions } from '../../state/modules/app';
import { AlertType } from '../../graphql/schema/p4types';
import { useLazyQuery } from '@apollo/client';
import GetSaveJobInfo from '../../graphql/queries/system/GetSaveJobInfo.graphql';
import isPromise from '../../utils/isPromise';
import { IConnectedFormModals } from '../Context/ConnectedFormContext';
import { SubmitOption } from '../../@types/connectedFormTypes';
import { IReduxStore } from '../../@types/reduxTypes';
import { useP4ToastController } from '../../views/system/NotificationsArea';
import { IActionType } from './Hooks/usePerformSave';

export type IAfterSubmit = (meta: { result?: any }) => void;

export type IAfterPostSave = (
  afterPostSave: IAfterSubmit,
  { action, result }: { action: IActionType; result: any }
) => void;

type IManagedSubmitOptions = {
  name: string;
  afterSubmit: IAfterSubmit;
  modal: IConnectedFormModals;
  submitOptions: [
    selectedSubmitOption: { value: SubmitOption },
    dispatchSubmitOption: (action: { type: string }) => void
  ];
  postSaveFunction: IAfterPostSave;
};

export const useSaveJobInfo = ({ submitInProgress }) => {
  const { dispatchJobToast, dispatchSimpleToast } = useP4ToastController();
  const lastMutationId = useRef<string | null>(null);
  const jobMap = useSelector((store: IReduxStore) => store.app.jobMap);
  const jobs = useRef(jobMap);
  useEffect(() => {
    jobs.current = jobMap; //Need to copy into a mutable field or complete/fail functions won't have have the up to date jobmap when accessing it.
  }, [jobMap]);

  //Need to access up to date state, so using the 'useRefFunction' hook to make sure this function stays up to date when accessed.
  const onCompleted = useRefFunction((jobData: any) => {
    if (submitInProgress && jobVariables?.mutationId === lastMutationId.current) {
      if (jobData?.jobInfo) {
        dispatchJobToast(jobData.jobInfo);
      } else if (tries.current < 3) {
        setTimeout(() => {
          getSaveJobInfo({
            variables: jobVariables,
          });
        }, 3500);
        tries.current++;
      } else {
        dispatchSimpleToast('Save Progress', 'Unable to determine save progress', AlertType.Warning);
      }
    }
  });

  const tries = useRef(0);
  const [getSaveJobInfo, { variables: jobVariables }] = useLazyQuery(GetSaveJobInfo, {
    fetchPolicy: 'network-only',
    onCompleted: onCompleted,
  });

  const submitInitiated = useCallback((mutationId: string) => {
    lastMutationId.current = mutationId;
    tries.current = 0;

    setTimeout(() => {
      getSaveJobInfo({
        variables: {
          mutationId: mutationId,
        },
      });
    }, 1500);
  }, []);

  const markJobAsCompleted = useCallback((failed = false) => {
    Object.keys(jobs.current).forEach((jobKey) => {
      const job = jobs.current[jobKey];

      if (job.clientMutationId === lastMutationId.current) {
        const updatedJob = { ...job };
        updatedJob.complete = true;
        updatedJob.failed = failed;
        dispatch(appActions.updateJobStatus(updatedJob));
      }
    });
  }, []);

  return useMemo(
    () => ({ submitInitiated, lastMutationId, markJobAsCompleted }),
    [submitInitiated, lastMutationId, markJobAsCompleted]
  );
};

/**
 * Extracted code for job-based submit handling to it's own hook for readability purposes. Only intended for use in ConnectedForm.js
 */
export const useManagedSubmit = ({
  name,
  afterSubmit,
  modal,
  submitOptions,
  postSaveFunction,
}: IManagedSubmitOptions) => {
  const submitPromise = useRef<Promise<any> | null>(null);
  const [submitInProgress, setSubmitInProgress] = useState(false); //Used to disable the 'submit' button.

  const { dispatchSimpleToast } = useP4ToastController();

  const { submitInitiated, markJobAsCompleted } = useSaveJobInfo({ submitInProgress });

  const submitCompleted = (props?: { result: any; action: any }) => {
    const { result, action } = props || {};

    submitPromise.current = null;
    setSubmitInProgress(false);
    markJobAsCompleted(false);

    //If a postSaveFunction is provided by the form, it is handling it's own 'submit-completion' logic so shortcut out here.
    if (result && postSaveFunction && !action?.submitMeta?.suppressPostSave) {
      const afterSubmitWithSuppressCheck: IAfterSubmit = (meta) => {
        const suppressAfterSubmit = action?.submitMeta?.suppressAfterSubmit || action?.performSave;
        if (!suppressAfterSubmit) {
          afterSubmit(meta);
        }
      };

      postSaveFunction(afterSubmitWithSuppressCheck, { action, result });
      return result;
    }

    //If we've specified to suppress afterSubmit behaviours, or we've set 'performSave' on the action (Which is always a precursor to some other task, such as posting a record,
    // then we should suppress standard 'afterSubmit' behaviours (I.e. navigating back).
    const suppressAfterSubmit = action?.submitMeta?.suppressAfterSubmit || action?.performSave;

    const hasModalActions =
      modal &&
      result?.entityMetaData?.actions?.some?.((action: any) => Object.keys(modal).indexOf(action.actionType) > -1);

    if (
      result && //Must have a result to consider processing 'afterSubmit' logic.
      !suppressAfterSubmit &&
      !hasModalActions //Must not be suppressing after submit (If we are, it means there's a followup action, such as 'Post Invoice').
    ) {
      afterSubmit({ result });
    } else if (hasModalActions && suppressAfterSubmit) {
      //If our result has modal actions & we want to suppress 'afterSubmit' (back navigation) logic, currently our only path is to alter the submitOptions type.
      //(There's some potential to re-factor how the modals are loaded by using a state hook in connectedForm rather than redux form values...But out of scope right now & adds
      // further risk of issues).
      submitOptions?.[1]?.({ type: 'CONTINUE' });
    }

    return result;
  };

  const submitFailed = (error: any, { alertRequired = true } = {}) => {
    submitPromise.current = null;
    setSubmitInProgress(false);
    markJobAsCompleted(true);

    if (alertRequired) {
      dispatchSimpleToast(`${name} could not be updated`, `Error during save ${error}`, AlertType.Error);
    }
  };

  const listenToSubmit = (promise: Promise<any>) => {
    if (!isPromise(promise)) {
      setSubmitInProgress(false);
      return promise;
    }
    submitPromise.current = promise;
    return promise.then(submitCompleted, submitFailed);
  };

  return {
    submitPromise,
    submitInProgress,
    setSubmitInProgress,
    submitFunctions: { submitInitiated, submitCompleted, submitFailed }, //Required for post action buttons to work when they save ahead of posting. (Move initiated into here?).
    listenToSubmit,
  };
};
